import { useCallback, useEffect, useState } from "react";

const DB_NAME = "inkle_onboarding";
const DB_VERSION = 1;
const STORE_NAME = "entities";

// Helper to convert File to storable format
const fileToStorable = async (
  file: File | null
): Promise<{
  name: string;
  type: string;
  size: number;
  lastModified: number;
  arrayBuffer: ArrayBuffer;
} | null> => {
  if (!file) return null;
  const arrayBuffer = await file.arrayBuffer();
  return {
    name: file.name,
    type: file.type,
    size: file.size,
    lastModified: file.lastModified,
    arrayBuffer,
  };
};

// Helper to convert storable format back to File
const storableToFile = (
  storable: {
    name: string;
    type: string;
    size: number;
    lastModified: number;
    arrayBuffer: ArrayBuffer;
  } | null
): File | null => {
  if (!storable) return null;
  return new File([storable.arrayBuffer], storable.name, {
    type: storable.type,
    lastModified: storable.lastModified,
  });
};

// Initialize IndexedDB
const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
};

// Get data from IndexedDB
const getFromDB = async <T>(key: string): Promise<T | null> => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn(`Failed to get from IndexedDB for key "${key}":`, error);
    return null;
  }
};

// Save data to IndexedDB
const saveToDB = async <T>(key: string, data: T): Promise<void> => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(data, key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn(`Failed to save to IndexedDB for key "${key}":`, error);
  }
};

// Remove data from IndexedDB
const removeFromDB = async (key: string): Promise<void> => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn(`Failed to remove from IndexedDB for key "${key}":`, error);
  }
};

export const useEntitiesIndexedDB = <T>(
  key: string,
  initialValue: T
): {
  localData: T;
  setItem: (data: T) => void;
  removeItem: () => void;
  isLoading: boolean;
} => {
  const [localData, setLocalData] = useState<T>(initialValue);
  const [isLoading, setIsLoading] = useState(true);

  // Load initial data from IndexedDB - only run once on mount
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        const data = await getFromDB<T>(key);
        if (isMounted) {
          if (data !== null) {
            setLocalData(data);
          }
          setIsLoading(false);
        }
      } catch (error) {
        console.error(
          `useEntitiesIndexedDB - Error loading data for key "${key}":`,
          error
        );
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [key]);

  const setItem = useCallback(
    async (data: T) => {
      try {
        await saveToDB(key, data);
        setLocalData(data);
      } catch (error) {
        console.warn(`Failed to save to IndexedDB:`, error);
      }
    },
    [key]
  );

  const removeItem = useCallback(async () => {
    try {
      await removeFromDB(key);
      setLocalData(initialValue);
    } catch (error) {
      console.warn(`Failed to remove from IndexedDB:`, error);
    }
  }, [key, initialValue]);

  return { localData, setItem, removeItem, isLoading };
};
